import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import FacebookPixel from '@/components/FacebookPixel';
import ChatbotAGRP from '@/components/ChatbotAGRP';
import Footer from '@/components/Footer';
import '@/App.css';

// Pages
import AuthPage from '@/pages/AuthPage';
import HomePage from '@/pages/HomePage';
import DashboardPage from '@/pages/DashboardPage';
import VideoPlayerPage from '@/pages/VideoPlayerPage';
import MyPurchasesPage from '@/pages/MyPurchasesPage';
import LivePage from '@/pages/LivePage';
import BoutiquePage from '@/pages/BoutiquePage';
import ArtistDashboard from '@/pages/ArtistDashboard';
import AdminPanel from '@/pages/AdminPanel';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import ContactPage from '@/pages/ContactPage';
import CreatorSpacePage from '@/pages/CreatorSpacePage';
import RedevancePage from '@/pages/RedevancePage';
import AnalytiquePage from '@/pages/AnalytiquePage';
import DistributionServicePage from '@/pages/services/DistributionServicePage';
import ArtistChannelServicePage from '@/pages/services/ArtistChannelServicePage';
import AdSenseServicePage from '@/pages/services/AdSenseServicePage';
import YoutubeServicePage from '@/pages/services/YoutubeServicePage';
import VevoServicePage from '@/pages/services/VevoServicePage';
import BoostVideoServicePage from '@/pages/services/BoostVideoServicePage';
import LiveStreamingServicePage from '@/pages/services/LiveStreamingServicePage';
import SpotifyPromotionPage from '@/pages/services/SpotifyPromotionPage';
import AppleMusicPromotionPage from '@/pages/services/AppleMusicPromotionPage';
import BoomplayPromotionPage from '@/pages/services/BoomplayPromotionPage';
import TidalPromotionPage from '@/pages/services/TidalPromotionPage';
import AdvertisementsManagementPage from '@/pages/AdvertisementsManagementPage';
import LiveStreamManagementPage from '@/pages/LiveStreamManagementPage';
import MessagesPage from '@/pages/MessagesPage';
import NotificationsPage from '@/pages/NotificationsPage';
import VipSmmServicesPage from '@/pages/VipSmmServicesPage';
import AdminBoostPricesPage from '@/pages/AdminBoostPricesPage';
import StudioMusicalPage from '@/pages/StudioMusicalPage';
import AboutPage from '@/pages/AboutPage';
import VendreVideosPage from '@/pages/VendreVideosPage';
import VendreVideosPageSimple from '@/pages/VendreVideosPageSimple';
import VendreLivresPage from '@/pages/VendreLivresPage';
import VendezVideoPage from '@/pages/VendezVideoPage';
import AdminVideosPage from '@/pages/AdminVideosPage';
import AutoLoginPage from '@/pages/AutoLoginPage';

// Protected Route component - OPTIMISÉ (pas d'écran noir)
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  // Loading léger sans bloquer l'interface
  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#E50914] border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Auto Login - Connexion automatique par lien */}
      <Route path="/auto-login" element={<AutoLoginPage />} />
      
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/contact" element={<ContactPage />} />
      
      {/* Dashboard Principal - Page par défaut après login */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        } 
      />
      
      {/* Boutique - PAGE PUBLIQUE - Tous les clips visibles */}
      <Route 
        path="/boutique" 
        element={<BoutiquePage />} 
      />
      
      {/* Page d'accueil = Boutique (clips publics) */}
      <Route
        path="/"
        element={<BoutiquePage />}
      />
      
      {/* Services Boost VipSmmPro - PAGE PUBLIQUE */}
      <Route
        path="/services-boost"
        element={<VipSmmServicesPage />}
      />
      
      <Route
        path="/video/:id"
        element={
          <ProtectedRoute>
            <VideoPlayerPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/purchases"
        element={
          <ProtectedRoute>
            <MyPurchasesPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationsPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/live"
        element={
          <ProtectedRoute>
            <LivePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly={true}>
            <AdminPanel />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/mon-espace-artiste"
        element={
          <ProtectedRoute>
            <ArtistDashboard />
          </ProtectedRoute>
        }
      />
      
      {/* Alias pour le formulaire de vente vidéos */}
      <Route
        path="/publier"
        element={
          <ProtectedRoute>
            <ArtistDashboard />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/espace-createur"
        element={
          <ProtectedRoute>
            <CreatorSpacePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin-panel"
        element={
          <ProtectedRoute adminOnly={true}>
            <AdminPanel />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/redevance"
        element={
          <ProtectedRoute>
            <RedevancePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/analytique"
        element={
          <ProtectedRoute>
            <AnalytiquePage />
          </ProtectedRoute>
        }
      />
      
      {/* Services avec formulaires */}
      {/* Services - URLs Uniques pour Pubs Facebook */}
      <Route
        path="/distribuer-musique"
        element={
          <ProtectedRoute>
            <DistributionServicePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/chaine-artiste-verifiee"
        element={
          <ProtectedRoute>
            <ArtistChannelServicePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/creer-adsense"
        element={
          <ProtectedRoute>
            <AdSenseServicePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/acheter-chaine-monetisee"
        element={
          <ProtectedRoute>
            <YoutubeServicePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/configurer-vevo"
        element={
          <ProtectedRoute>
            <VevoServicePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/promouvoir-video"
        element={
          <ProtectedRoute>
            <BoostVideoServicePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/live-streaming"
        element={
          <ProtectedRoute>
            <LiveStreamingServicePage />
          </ProtectedRoute>
        }
      />

      {/* Services de Promotion Streaming */}
      <Route
        path="/promouvoir-spotify"
        element={
          <ProtectedRoute>
            <SpotifyPromotionPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/promouvoir-apple-music"
        element={
          <ProtectedRoute>
            <AppleMusicPromotionPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/promouvoir-boomplay"
        element={
          <ProtectedRoute>
            <BoomplayPromotionPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/promouvoir-tidal"
        element={
          <ProtectedRoute>
            <TidalPromotionPage />
          </ProtectedRoute>
        }
      />

      {/* Anciennes routes (garder pour compatibilité) */}
      <Route
        path="/service/distribution"
        element={
          <ProtectedRoute>
            <DistributionServicePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/service/artist-channel"
        element={
          <ProtectedRoute>
            <ArtistChannelServicePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/service/adsense"
        element={
          <ProtectedRoute>
            <AdSenseServicePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/service/youtube"
        element={
          <ProtectedRoute>
            <YoutubeServicePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/service/vevo"
        element={
          <ProtectedRoute>
            <VevoServicePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/service/boost"
        element={
          <ProtectedRoute>
            <BoostVideoServicePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/service/live-streaming"
        element={
          <ProtectedRoute>
            <LiveStreamingServicePage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/advertisements"
        element={
          <ProtectedRoute adminOnly={true}>
            <AdvertisementsManagementPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/live-streams"
        element={
          <ProtectedRoute adminOnly={true}>
            <LiveStreamManagementPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/boost-prices"
        element={
          <ProtectedRoute adminOnly={true}>
            <AdminBoostPricesPage />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      
      {/* Studio Musical - Page publique */}
      <Route
        path="/studio-musical"
        element={<StudioMusicalPage />}
      />
      
      {/* À propos - Page publique */}
      <Route
        path="/a-propos"
        element={<AboutPage />}
      />
      
      {/* Vendre mes vidéos - Page protégée */}
      {/* Page publique: Vendez vidéo sur AGRP Box */}
      <Route
        path="/vendez-video"
        element={<VendezVideoPage />}
      />
      
      {/* Formulaire upload vidéo (nécessite connexion) */}
      <Route
        path="/vendre-videos"
        element={
          <ProtectedRoute>
            <VendreVideosPage />
          </ProtectedRoute>
        }
      />
      
      {/* Formulaire upload livres/ebooks (nécessite connexion) */}
      <Route
        path="/vendre-livres"
        element={
          <ProtectedRoute>
            <VendreLivresPage />
          </ProtectedRoute>
        }
      />
      
      {/* Admin: Gestion des vidéos */}
      <Route
        path="/admin/videos"
        element={
          <ProtectedRoute>
            <AdminVideosPage />
          </ProtectedRoute>
        }
      />
      
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <HelmetProvider>
        <AuthProvider>
          <div className="App">
            <AppRoutes />
            <ChatbotAGRP />
            
            {/* Footer global - Visible partout même sans être connecté */}
            <Footer />
            
            <Toaster 
              position="top-right" 
              theme="dark"
              toastOptions={{
                style: {
                  background: '#121212',
                  color: '#fff',
                  border: '1px solid #27272A'
                }
              }}
            />
          </div>
        </AuthProvider>
      </HelmetProvider>
    </BrowserRouter>
  );
}

export default App;
